import moment from 'moment';

// Without a date filter, Day / Week / Month decide what the cards show:
// today, this week (Mon→today) or this month (1st→today), each compared with the
// same days one period earlier. The page asks /api/dashboard for one daily series
// covering the chart window and derives everything here. All dates are UTC.

const DMY = 'DD/MM/YYYY';
const YMD = 'YYYY-MM-DD';
const METRICS = ['newUsers', 'songAdded', 'songRated'];

const unitOf = g => (g === 'week' ? 'isoWeek' : g);

// The daily range to request for each granularity: 30 days, 12 weeks or 12 months.
export function windowFor(granularity, today = moment.utc()) {
    const end = today.clone().startOf('day');
    let start;
    if (granularity === 'week') start = end.clone().startOf('isoWeek').subtract(11, 'weeks');
    else if (granularity === 'month') start = end.clone().startOf('month').subtract(11, 'months');
    else start = end.clone().subtract(29, 'days');
    return [start.format(DMY), end.format(DMY)];
}

function pctChange(value, previous) {
    if (!previous) return null;
    return Math.round(((value - previous) / previous) * 1000) / 10;
}

function sumRange(byDay, from, to, key) {
    let n = 0;
    for (let d = from.clone(); !d.isAfter(to); d.add(1, 'day')) {
        const p = byDay.get(d.format(YMD));
        if (p) n += p[key];
    }
    return n;
}

const fmtRange = (a, b) => (a.isSame(b, 'day') ? a.format('D MMM') : `${a.format('D')}–${b.format('D MMM')}`);

// d is the /api/dashboard response for the window above (granularity 'day').
export function buildView(d, granularity) {
    const end = moment.utc(d.period.end, YMD);
    let cur, prev, label, vs, growth;
    if (granularity === 'week') {
        cur = [end.clone().startOf('isoWeek'), end];
        prev = [cur[0].clone().subtract(7, 'days'), end.clone().subtract(7, 'days')];
        label = `This week (${fmtRange(cur[0], cur[1])})`;
        vs = 'vs same days last week';
        growth = 'growth this week';
    } else if (granularity === 'month') {
        cur = [end.clone().startOf('month'), end];
        const prevStart = cur[0].clone().subtract(1, 'month');
        const sameDay = prevStart.clone().add(end.date() - 1, 'days');
        const prevMonthEnd = prevStart.clone().endOf('month').startOf('day');
        prev = [prevStart, sameDay.isAfter(prevMonthEnd) ? prevMonthEnd : sameDay];
        label = `This month (${fmtRange(cur[0], cur[1])})`;
        vs = 'vs same days last month';
        growth = 'growth this month';
    } else {
        cur = [end, end];
        prev = [end.clone().subtract(1, 'day'), end.clone().subtract(1, 'day')];
        label = 'Today';
        vs = 'vs yesterday';
        growth = 'growth today';
    }

    const byDay = new Map(d.series.points.map(p => [p.bucket, p]));
    const cards = {};
    METRICS.forEach(k => {
        const value = sumRange(byDay, cur[0], cur[1], k);
        const previous = sumRange(byDay, prev[0], prev[1], k);
        cards[k] = { value, previous, pctChange: pctChange(value, previous) };
    });
    // Total at the end of the window vs total just before the current period began.
    const totalEnd = d.cards.totalUsers.value;
    const totalBefore = totalEnd - cards.newUsers.value;
    cards.totalUsers = { value: totalEnd, previous: totalBefore, pctChange: pctChange(totalEnd, totalBefore) };

    // Regroup the daily points into the chosen buckets for the chart.
    const buckets = new Map();
    d.series.points.forEach(p => {
        const key = moment.utc(p.bucket, YMD).startOf(unitOf(granularity)).format(YMD);
        if (!buckets.has(key)) buckets.set(key, { bucket: key, newUsers: 0, songAdded: 0, songRated: 0 });
        METRICS.forEach(k => { buckets.get(key)[k] += p[k]; });
    });
    const points = Array.from(buckets.values()).sort((a, b) => (a.bucket < b.bucket ? -1 : 1));
    const chartLabel = { day: 'trend: last 30 days, by day', week: 'trend: last 12 weeks, by week', month: 'trend: last 12 months, by month' }[granularity] || 'trend: last 30 days, by day';

    return {
        label,
        cards,
        compare: { totalUsers: growth, newUsers: vs, songAdded: vs, songRated: vs },
        chartLabel,
        series: { granularity, start: d.series.start, end: d.series.end, points },
        csvPeriod: `${cur[0].format(YMD)} to ${cur[1].format(YMD)} vs ${prev[0].format(YMD)} to ${prev[1].format(YMD)}`
    };
}

// Chart grouping for a custom range, so the chart never has too few or too many points.
export function granularityForRange(range) {
    const days = moment.utc(range[1], DMY).diff(moment.utc(range[0], DMY), 'days') + 1;
    if (days <= 62) return 'day';
    if (days <= 190) return 'week';
    return 'month';
}
